/** Audio operations API */
import { getAPI, token } from "@/api/params.js";
import { parseJsonResponse } from "@/api/common.js";

import { MEDIA_UPDATE_INTERVAL, MEDIA_CONVERSION_STATUS } from "@/constants.js";

/** update callbacks */
let updateCallback = null;
let errorCallback = null;

/** setTimeout ID to stop updating process */
let updateKey = null;

function getFullTokenedUrl(method, params = {}) {
  const url = new URL(`${getAPI()}/audio/${method}`);
  for (let key in params) {
    url.searchParams.append(key, params[key]);
  }

  url.searchParams.append("token", token);

  return url.toString();
}

/**
 * Convert audio item data from server format to client format
 * @param {Object} itemData server format data
 * @returns {Object} client format data
 */
export const parseAudioItem = (itemData) => {
  return {
    id: itemData.id,
    title: itemData.tags.title ?? 'Add title',
    status: itemData.status ?? MEDIA_CONVERSION_STATUS.READY,
    progress: itemData.conversionPercentage ?? 0,
    isRemovable: itemData.canBeRemoved ?? true,
    startTime: +itemData.startTime ?? 0,
    endTime: +itemData.endTime ?? 0,
    downloadLink: itemData.remoteInfo ? itemData.remoteInfo.downloadUrl : itemData.storageUrl +'/'+ itemData.downloadLink,
    storageUrl: itemData.storageUrl,
    stream: itemData.stream,
    playLink: itemData.mp3Link ?? itemData.playLink,
    createdAt: +itemData.createdAt || 0,
    metadata: itemData.metadata ?? {},
    isUploadedByUser: itemData.isUploadedByUser,
    // isUploadedToMangomolo: itemData.isUploadedToMangomolo,
    sharings: itemData.sharings || []
  }
};

/**
 * Get audios list from server
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @returns {Object} audio items object
 */
async function fetchAudios(app, stream) {
  const fetchUrl = getFullTokenedUrl("", { app, stream });
  const resp = await fetch(fetchUrl);

  const parsed = await parseJsonResponse(resp);
  if (!parsed.success && parsed.success != undefined) {
    throw new Error(parsed.error);
  }
  return parsed;
}

async function fetchAudiosbyId(id) {
  const fetchUrl = getFullTokenedUrl(id);
  const resp = await fetch(fetchUrl);

  const parsed = await parseJsonResponse(resp);
  if (!parsed.success && parsed.success != undefined) {
    throw new Error(parsed.error);
  }
  return parsed;
}

/**
 * Convert server audio items data to clent format array
 * @param {Object} data audio items in server format
 * @returns {Array} audio items in client format
 */
function parseAudios(data) {
  const audios = data.items.map(parseAudioItem);
  return audios;
}

/**
 * Get audios from server
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @returns {Array} audio items
 */
export const getAudios = async (app, stream) => {
  const audiosResp = await fetchAudios(app, stream);
  return parseAudios(audiosResp);
};

export const getAudioById = async (id) => {
  const audiosResp = await fetchAudiosbyId(id);
  return audiosResp
}

/**
 * Update audios and call update-handler
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
async function updateAudios(app, stream) {
  try {
    const audios = await getAudios(app, stream);

    if (updateCallback) {
      updateCallback(audios);
    }
  } catch (err) {
    console.error(`Update audios error: ${err}`);
    if (errorCallback) {
      errorCallback(err);
    }
  }
}
export const uploadToMangomoloAudio = async (
  audioId,
  mangomoloLink,
  title,
  title_arabic,
  description,
  stream,
  categoryId,
  mmSourceAudioId,
  multistepId,
  target_channel_id,
  publish
) => {
  const linkURL = `${getAPI()}/audio/${audioId}/share/mangomolo?token=${token}${
    multistepId ? "&multistepId=" + multistepId : ""
  }`;
  const resp = await fetch(linkURL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      audioId,
      title,
      title_arabic,
      description,
      stream,
      multistepId,
      categoryId,
      mmSourceAudioId,
      mangomoloLink,
      target_channel_id,
      publish
    })
  });
  let response = await resp.json();
  if (!resp.ok) {
    throw new Error(`${resp.status}: ${resp.statusText}`);
  }
  return response
};

/**
 * Update audios and set timeout for next updating
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
async function startUpdateAudios(app, stream) {
  await updateAudios(app, stream);
  setTimeout(() => startUpdateAudios(app, stream), MEDIA_UPDATE_INTERVAL);
}

/**
 * Set callback function that will be executed every time when audios have updated
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @param {Object} options object with 2 keys:
 *  callback(videos) - callback function that handles new videos array
 *  error(err) - callback function that handles updating error
 */
export const subscribeToAudioUpdates = (app, stream, callback, error ) => {
  if (updateKey !== null) {
    unsubscribeFromAudioUpdates();
  }
  updateCallback = callback;
  errorCallback = error;

  startUpdateAudios(app, stream);
};

/**
 * Reset callback function
 */
export const unsubscribeFromAudioUpdates = () => {
  clearInterval(updateKey);
  updateKey = null;
};

/**
 * Delete audio item
 * @param {Number} id audio item ID
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const removeAudio = async (id, app, stream) => {
  const removeUrl = getFullTokenedUrl(id);
  const removeResp = await fetch(removeUrl, { method: "DELETE" });

  const { success, error } = await parseJsonResponse(removeResp);
  if (!success) {
    throw new Error(error);
  }

  updateAudios(app, stream);
};

/**
 * Delete all videos for channel
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const removeAllAudios = async (app, stream) => {
  if (stream === "No Stream") {
    stream = "";
  }
  const removeAllUrl = getFullTokenedUrl("", { app, stream });
  const removeAllResp = await fetch(removeAllUrl, { method: "DELETE" });
  const { success, error } = await parseJsonResponse(removeAllResp);

  if (!success) {
    throw new Error(error);
  }

  updateAudios(app, stream);
};

/**
 * Change audio item title
 * @param {Number} id audio item ID
 * @param {String} title new item title
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const setAudioTitle = async (id, title, app, stream) => {
  const updateUrl = getFullTokenedUrl(id);
  const updateResp = await fetch(updateUrl, {
    method: "PUT",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      title,
    }),
  });

  const { success, error } = await parseJsonResponse(updateResp);
  if (!success) {
    throw new Error(error);
  }

  updateAudios(app, stream);
};

/**
 * Clip video from stream
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @param {Number} start Start time timstamp
 * @param {Number} end End time timestamp
 * @param {String} title Generating video's name
 */
export const cropAudio = async (app, stream, start, end, title, isMultistepSource) => {
  const cropUrl = getFullTokenedUrl("generate");
  const cropData = {
    app: app,
    stream: stream,
    startTime: start,
    endTime: end,
    tags: {
      title: title
    },
    isMultistepSource: isMultistepSource,
  };
  const cropResp = await fetch(cropUrl, {
    method: "POST",
    body: JSON.stringify(cropData),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  const { success, error } = await parseJsonResponse(cropResp);

  if (!success) {
    throw new Error(error);
  }

  updateAudios(app, stream);
};
